import * as React from 'react';
import * as stableSort from 'stable';

import App from 'src/state/App';
import Calculator from 'src/state/Calculator';
import Room from 'src/state/Room';
import RoomPage from 'src/state/RoomPage';
import Game from 'src/state/Game';
import Player from 'src/state/Player';
import { assertType, Ref, RefSet } from 'src/util';

import SelectorDialog from '../SelectorDialog';

type SDT = {
  type: 'PlayerSelector',
  option: Player,
  metadata: {
    side: 0 | 1,
  },
};

export type State = SelectorDialog.State<SDT>;

function SuggestedPlayers(room: Room, calculator: Calculator) {
  const players = Room.Players(room);

  const suggestions: Game.Suggestion[] = Room.SuggestedGames(room, calculator);

  const valuedPlayers = (players
    .map(p => {
      const firstSuggestion = (suggestions
        .find(s => (
          Ref.equals(p.ref, s.players[0]) ||
          Ref.equals(p.ref, s.players[1])
        ))
      );

      const { value } = (firstSuggestion || { value: Infinity });

      return { player: p, value };
    })
  );

  return (stableSort(valuedPlayers, ({ value: a }, { value: b }) => b - a)
    .map(({ player }) => player)
  );
}

function SuggestedOpponents(
  room: Room,
  calculator: Calculator,
  p: Ref<Player>,
) {
  const players = Room.Players(room);

  const suggestions: Game.Suggestion[] = Room.SuggestedGames(room, calculator);

  const valuedPlayers = (players
    .filter(opp => !Ref.equals(opp.ref, p))
    .map(opp => {
      const firstSuggestion = (suggestions
        .find(s => (
          (
            Ref.equals(s.players[0], p) ||
            Ref.equals(s.players[1], p)
          ) &&
          (
            Ref.equals(s.players[0], opp.ref) ||
            Ref.equals(s.players[1], opp.ref)
          )
        ))
      );

      const { value } = (firstSuggestion || { value: Infinity });

      return { opponent: opp, value };
    })
  );

  return (stableSort(valuedPlayers, ({ value: a }, { value: b }) => b - a)
    .map(({ opponent }) => opponent)
  );
}

export const Component: React.SFC<{
  store: App.Store,
  room: Room,
  page: RoomPage,
  localState: SelectorDialog.State<SDT>,
}> = ({ store, room, page, localState }) => {
  const { calculator } = store.getState();

  const dispatch = store.dispatchCurrentRoomPage;
  const hideDialog = () => RoomPage.hideDialog(dispatch);

  const playerSide = localState.metadata.side;
  const opponentRef = Player.Pair.Opponent(page.selectedPlayers, playerSide);

  const opponent = (
    opponentRef &&
    RefSet.resolve(room.entities.players, opponentRef)
  );

  const unmatchedSuggestions = (
    (
      opponentRef ?
      SuggestedOpponents(room, calculator, opponentRef) :
      SuggestedPlayers(room, calculator)
    )
    .map(player => {
      const newPlayerText = localState.data.newOptionText;
      const result = { player, match: false };

      if (!newPlayerText) {
        return result;
      }

      if (
        // TODO: Use fuzzy matching
        player.name.toLowerCase().indexOf(newPlayerText.toLowerCase()) === -1
      ) {
        return result;
      }

      return { ...result, match: true };
    })
  );

  const suggestions = (
    stableSort(
      unmatchedSuggestions,
      ({ match: a }, { match: b }) => Number(b) - Number(a),
    )
    .map(({ player }) => player)
  );

  return SelectorDialog<SDT>({
    title: `Select ${opponent ? opponent.name + '\'s Opponent' : 'Player'}`,
    options: suggestions,
    enableNew: true,
    dialogState: localState,
    OptionName: player => player.name,
    selectionValid: (selection) => {
      const { newOptionText } = localState.data;

      if (selection.type === 'new' && newOptionText === '') {
        return false;
      }

      return true;
    },
    onSelection: (selection) => {
      const check = (() => {
        switch (selection.type) {
          case 'new': {
            const newPlayer = Player({ name: localState.data.newOptionText });

            store.dispatchRoom(room.ref, {
              type: 'add-player',
              data: newPlayer,
            });

            dispatch({
              type: 'select-player',
              player: newPlayer.ref,
              side: playerSide,
            });

            return null;
          }

          case 'existing': {
            dispatch({
              type: 'select-player',
              player: selection.data.ref,
              side: localState.metadata.side,
            });

            return null;
          }

          case 'none': {
            dispatch({
              type: 'select-player',
              player: null,
              side: localState.metadata.side,
            });

            return null;
          }
        }
      })();

      assertType<null>(check);
    },
    dispatch: newState => dispatch({
      type: 'set-dialog',
      data: newState,
    }),
    hideDialog,
    isHidden: () => page.dialog.hidden,
  });
};



// WEBPACK FOOTER //
// ./src/ui/PlayerSelector/Dialog.tsx